/* eslint-disable @typescript-eslint/no-explicit-any */
import { Logger } from '@nestjs/common';
import {
  ENUM_STATUS,
  ENUM_PHASE,
  type Action,
  type ActionHandler,
  type ActionMetadata,
  ENUM_ERROR,
  ActionContext,
  TLogContent,
  ILogAttributes,
  ILogContentParsed,
} from './types';
import { convertActionContextToReportData } from './utils';

const INTERNAL = 'AUTOMATION_INTERNAL';

/**
 * TriggerClient
 *
 * 自动化触发器管理客户端核心类
 *
 * 负责管理触发器注册、执行和生命周期事件
 * 提供中间件支持和与 trigger-server 的回调集成
 */
export class TriggerClient {
  private _triggerActionMap: Map<string, Action> = new Map();
  private _logger: Logger;

  /**
   * 创建 TriggerClient 实例
   *
   * @param logger - 日志记录器
   */
  public constructor(logger: Logger) {
    this._logger = logger;
  }

  /**
   * 为指定的 trigger 注册动作
   *
   * @param trigger - 触发器标识
   * @param action - 动作配置
   */
  public registerAction(trigger: string, action: Action): void {
    this._logger.debug(
      `[${INTERNAL}] Registering action for trigger: ${trigger}`,
      {
        actionName: action.name,
        path: action.path,
        desc: action.desc,
      }
    );

    if (this._triggerActionMap.has(trigger)) {
      this._logger.debug(
        `[${INTERNAL}] Trigger ${trigger} already registered, overwriting`
      );
    }

    const wrappedHandler = this._wrapHandler(action.handler);
    const wrappedAction: Action = {
      ...action,
      handler: wrappedHandler,
    };

    this._triggerActionMap.set(trigger, wrappedAction);
    this._logger.debug(
      `[${INTERNAL}] Action registered successfully for trigger: ${trigger}`
    );
  }

  /**
   * 执行指定的 trigger 动作
   *
   * @param trigger - 触发器标识
   * @param instanceID - 实例ID
   * @param context - 执行上下文
   * @returns 执行报告数据
   */
  public executeAction(
    trigger: string,
    instanceID: string,
    context: ActionContext
  ): ActionContext {
    let input;

    // 解析输入参数
    try {
      const parsed = JSON.parse(context.content || '{}');
      input = parsed?.input ?? null;
    } catch (error) {
      input = undefined;
      const content = JSON.stringify({
        input,
        output: {
          error: ENUM_ERROR.PARAM_INVALID,
          message: `参数解析失败(trigger: ${trigger}, instanceID: ${instanceID})`,
        },
      });
      const actionCtx: ActionContext = {
        ...context,
        attributes: {
          ...context.attributes,
          phase: ENUM_PHASE.Trigger,
          status: ENUM_STATUS.FAILED,
          endAt: Date.now(),
          duration_ms: this._computeDuration(context.attributes.startAt),
        },
        content,
      };
      this.report(...convertActionContextToReportData(actionCtx));
      return actionCtx;
    }

    try {
      // 获取动作处理器
      const action = this.getAction(trigger);

      // 如果动作不存在，返回错误
      if (!action) {
        const actionCtx: ActionContext = {
          ...context,
          attributes: {
            ...context.attributes,
            phase: ENUM_PHASE.Trigger,
            status: ENUM_STATUS.FAILED,
            endAt: Date.now(),
            duration_ms: this._computeDuration(context.attributes.startAt),
          },
          content: JSON.stringify({
            input,
            output: {
              error: ENUM_ERROR.NOT_EXIST,
              message: `执行动作不存在: ${trigger}`,
            },
          }),
        };
        // 上报 trigger 结果{成功/失败}
        this.report(...convertActionContextToReportData(actionCtx));
        return actionCtx;
      }

      // 直接执行 _wrapHandler(handler)，不会等待执行结束
      action.handler(context);

      const actionCtx: ActionContext = {
        ...context,
        attributes: {
          ...context.attributes,
          phase: ENUM_PHASE.Trigger,
          status: ENUM_STATUS.SUCCEEDED,
          endAt: Date.now(),
          duration_ms: this._computeDuration(context.attributes.startAt),
        },
        content: JSON.stringify({ input, output: null }),
      };
      // 上报 trigger 结果{成功/失败}
      this.report(...convertActionContextToReportData(actionCtx));

      return actionCtx;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : String(error);

      const actionCtx: ActionContext = {
        ...context,
        attributes: {
          ...context.attributes,
          phase: ENUM_PHASE.Trigger,
          status: ENUM_STATUS.FAILED,
          endAt: Date.now(),
          duration_ms: this._computeDuration(context.attributes.startAt),
        },
        content: JSON.stringify({
          input,
          output: { error: ENUM_ERROR.TRY_CATCH, message: errorMessage },
        }),
      };
      // 上报 trigger 结果{成功/失败}
      this.report(...convertActionContextToReportData(actionCtx));

      return actionCtx;
    }
  }

  /**
   * 根据 trigger 获取对应的动作
   *
   * @param trigger - 触发器标识
   * @returns 动作实例，如果不存在返回 undefined
   */
  public getAction(trigger: string): Action | undefined {
    const action = this._triggerActionMap.get(trigger);

    if (!action) {
      this._logger.debug(`No action found for trigger: ${trigger}`);
      return undefined;
    }

    this._logger.debug(`Retrieved action for trigger: ${trigger}`, {
      actionName: action.name,
      path: action.path,
    });

    return action;
  }

  /**
   * 获取所有已注册的动作
   *
   * @returns 动作映射表
   */
  public getAllActions(): Map<string, Action> {
    return this._triggerActionMap;
  }

  /**
   * 根据 trigger 获取动作的元数据
   *
   * @param trigger - 触发器标识
   * @returns 动作元数据，如果不存在返回 undefined
   */
  public getActionMetadata(trigger: string): ActionMetadata | undefined {
    const action = this.getAction(trigger);

    if (!action) {
      return undefined;
    }

    return {
      name: action.name,
      path: action.path,
      desc: action.desc,
    };
  }

  /**
   * 获取所有已注册的 trigger 标识
   *
   * @returns trigger 标识数组
   */
  public getAllTriggers(): string[] {
    const triggers = Array.from(this._triggerActionMap.keys());
    this._logger.debug(`Retrieved all triggers`, {
      count: triggers.length,
    });
    return triggers;
  }

  /**
   * 根据 trigger 移除对应的动作
   *
   * @param trigger - 触发器标识
   * @returns 如果成功移除返回 true，如果不存在返回 false
   */
  public removeAction(trigger: string): boolean {
    const existed = this._triggerActionMap.has(trigger);

    if (existed) {
      this._triggerActionMap.delete(trigger);
      this._logger.debug(`Removed action for trigger: ${trigger}`);
    } else {
      this._logger.debug(
        `Attempted to remove non-existent trigger: ${trigger}`
      );
    }

    return existed;
  }

  /**
   * 清空所有已注册的动作
   */
  public clearAllActions(): void {
    const count = this._triggerActionMap.size;
    this._triggerActionMap.clear();
    this._logger.debug(`Cleared all actions`, { count });
  }

  /**
   * 上报执行数据，用于日志记录和调试
   */
  public report(msg: TLogContent, extra: ILogAttributes): void {
    const {
      status,
      phase,
      start_at,
      end_at,
      trigger,
      trigger_id,
      instance_id,
      trigger_type,
    } = extra;

    let contentObj: { input: any; output: any } = { input: null, output: null };
    try {
      contentObj = JSON.parse(msg);
    } catch (error) {
      this._logger.debug(`Failed to parse log content as JSON: ${msg}`);
    }

    const duration_ms = end_at && start_at ? end_at - start_at : 0;

    let error_message;
    if (status === ENUM_STATUS.FAILED && contentObj?.output?.message) {
      error_message = contentObj.output.message;
    }

    const logContent: ILogContentParsed = {
      trigger,
      trigger_id,
      trigger_type,
      instance_id,
      phase,
      status,
      duration_ms,
      content: contentObj,
      error_message,
    };

    // for 观测
    const signature = {
      sourceType: 'platform',
      paas_attributes_module: 'trigger',
    };
    const newMsg = JSON.stringify(logContent);

    // msg
    if (status === ENUM_STATUS.FAILED) {
      this._logger.error(newMsg, { ...extra, ...signature });
    } else {
      this._logger.log(newMsg, { ...extra, ...signature });
    }
  }

  /**
   * 包装处理器，添加日志记录和计时功能
   *
   * @param handler - 原始处理器
   * @returns 包装后的处理器
   */
  private _computeDuration(startAt?: number): number | undefined {
    return startAt ? Date.now() - startAt : undefined;
  }

  private _wrapHandler(handler: Action['handler']): ActionHandler {
    return async (context: ActionContext): Promise<ActionContext> => {
      let input;

      // 解析输入参数
      try {
        input = JSON.parse(context.content || '{}').input;
      } catch (error) {
        input = undefined;
      }

      try {
        const actionStartAt = Date.now();
        const output = await Promise.resolve(
          handler({
            attributes: {
              ...context.attributes,
              startAt: actionStartAt,
            },
            content: {
              input,
            },
          })
        );

        const actionCtx: ActionContext = {
          ...context,
          attributes: {
            ...context.attributes,
            startAt: actionStartAt,
            phase: ENUM_PHASE.Action,
            status: ENUM_STATUS.SUCCEEDED,
            endAt: Date.now(),
            duration_ms: this._computeDuration(actionStartAt),
          },
          content: JSON.stringify({
            input,
            output,
          }),
        };
        this.report(...convertActionContextToReportData(actionCtx));

        return actionCtx;
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : String(error);

        const actionCtx: ActionContext = {
          ...context,
          attributes: {
            ...context.attributes,
            phase: ENUM_PHASE.Action,
            status: ENUM_STATUS.FAILED,
            endAt: Date.now(),
            duration_ms: this._computeDuration(context.attributes.startAt),
          },
          content: JSON.stringify({
            input,
            output: { error: ENUM_ERROR.TRY_CATCH, message: errorMessage },
          }),
        };
        this.report(...convertActionContextToReportData(actionCtx));
        return actionCtx;
      }
    };
  }
}
